import { createSelector, type MemoizedSelector } from '@ngrx/store';

import { DEFAULT_COUNTRY_CODE } from '@ps/survey-data-access';
import {
  ChallengingQuota,
  FeasibilityEstimate,
  SurveyEpcValues,
  toChallengingQuotas,
  toDifficultQuotaIds,
  withoutIds,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  selectMetadataForm,
  selectQuotasForm,
} from '../audience/survey-creation-audience.selectors';
import { surveyCreationFeature } from '../survey-creation.feature';
import { selectActiveInsightsChild } from './utils/insights-children.helper';
import { DEFAULT_CHILD_INSIGHTS_STATE } from './survey-creation-insights-state.constants';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import {
  InsightsChildState,
  SurveyCreationPricingState,
} from './survey-creation-insights-state.model';
import { epcPlatformCountryCode } from './utils/survey-epc.helper';

const { selectInsights, selectCore } = surveyCreationFeature;

type InsightsChildSelector = MemoizedSelector<object, InsightsChildState>;

const selectActiveChild: InsightsChildSelector = createSelector(
  selectInsights,
  selectCore,
  (insights, core) => selectActiveInsightsChild(insights, core.activeChildKey),
);

// Any child by key, for work that runs on a child other than the active one.
const selectChildFor = (key: SurveyChildKey): InsightsChildSelector =>
  createSelector(
    selectInsights,
    (insights) => insights.children[key] ?? DEFAULT_CHILD_INSIGHTS_STATE,
  );

// Shared by the active child and any child by key.
const feasibilityCpiOf = (
  child: InsightsChildSelector,
): MemoizedSelector<object, number> =>
  createSelector(child, ({ feasibility }) => feasibility.cpi);

const pricingOf = (
  child: InsightsChildSelector,
): MemoizedSelector<object, SurveyCreationPricingState> =>
  createSelector(child, ({ pricing }) => pricing);

/** Every child, not just the active one — for work that runs per locale. */
export const selectInsightsChildren = createSelector(
  selectInsights,
  ({ children }) => children,
);

//SECTION SCORE
export const selectScorePercentage = createSelector(
  selectActiveChild,
  ({ score }) => score.scorePercentage,
);

export const selectScoreLoading = createSelector(
  selectActiveChild,
  ({ score }) => score.loading,
);

export const selectScoreError = createSelector(
  selectActiveChild,
  ({ score }) => score.error,
);
//!SECTION

//SECTION FEASIBILITY
export const selectFeasibilityLoading = createSelector(
  selectActiveChild,
  ({ feasibility }) => feasibility.loading,
);

export const selectFeasibilityError = createSelector(
  selectActiveChild,
  ({ feasibility }) => feasibility.error,
);

export const selectFeasibility = createSelector(
  selectActiveChild,
  ({ feasibility }): FeasibilityEstimate => ({
    total: feasibility.total,
    audience: feasibility.audience,
    quotaGroups: feasibility.quotaGroups,
    cpi: feasibility.cpi,
    suppliers: feasibility.suppliers,
  }),
);

export const selectAdvancedFiltersLookup = createSelector(
  selectActiveChild,
  ({ feasibility }) => feasibility.advancedFiltersLookup,
);

export const selectFeasibilityCpi = feasibilityCpiOf(selectActiveChild);

export const selectFeasibilitySuppliers = createSelector(
  selectActiveChild,
  ({ feasibility }) => feasibility.suppliers,
);

//!SECTION

//SECTION IMPROBABLE QUOTAS
// Internal: selectImprobableQuotas is the one public shape. Declared here so
// the difficult selector below can compose it without a temporal-dead-zone read.
const selectImprobableQuotaIds = createSelector(
  selectActiveChild,
  ({ improbableQuotas }) => improbableQuotas.quotaIds,
);

/** The buyer's quotas an operator's unicorn record matched. */
export const selectImprobableQuotas = createSelector(
  selectImprobableQuotaIds,
  selectQuotasForm,
  (improbableQuotaIds, quotasForm): ChallengingQuota[] =>
    toChallengingQuotas(new Set(improbableQuotaIds), quotasForm.value),
);
//!SECTION

//SECTION DIFFICULT QUOTAS
/**
 * Improbable quotas are removed: a quota is never flagged both amber and red,
 * and the stronger warning wins — legacy parity.
 */
export const selectDifficultQuotas = createSelector(
  selectFeasibility,
  selectQuotasForm,
  selectImprobableQuotaIds,
  ({ quotaGroups }, quotasForm, improbableQuotaIds): ChallengingQuota[] =>
    toChallengingQuotas(
      withoutIds(toDifficultQuotaIds(quotaGroups), improbableQuotaIds),
      quotasForm.value,
    ),
);
//!SECTION

//SECTION FLEXIBILITY PRICES
export const selectFlexibilityPrices = createSelector(
  selectActiveChild,
  ({ flexibilityPrices }) => flexibilityPrices.prices,
);

export const selectFlexibilityPricesError = createSelector(
  selectActiveChild,
  ({ flexibilityPrices }) => flexibilityPrices.error,
);
//!SECTION

//SECTION PRICING
export const selectPricing = pricingOf(selectActiveChild);
//!SECTION

//SECTION SIMILAR SURVEYS
export const selectSimilarSurveysState = createSelector(
  selectActiveChild,
  ({ similarSurveys }) => similarSurveys,
);

export const selectSimilarSurveys = createSelector(
  selectSimilarSurveysState,
  ({ surveys }) => surveys,
);

export const selectSimilarSurveysStatus = createSelector(
  selectSimilarSurveysState,
  ({ status }) => status,
);

export const selectSimilarSurveysError = createSelector(
  selectSimilarSurveysState,
  ({ error }) => error,
);

export const selectSimilarSurveysStale = createSelector(
  selectSimilarSurveysState,
  ({ isStale }) => isStale,
);

export const selectSimilarSurveysSearchedTargetingKey = createSelector(
  selectSimilarSurveysState,
  ({ searchedTargetingKey }) => searchedTargetingKey,
);

export const selectSimilarSurveysHasSearched = createSelector(
  selectSimilarSurveysSearchedTargetingKey,
  (searchedTargetingKey) => !!searchedTargetingKey,
);

export const selectSimilarSurveysSearchedGlobally = createSelector(
  selectSimilarSurveysState,
  ({ searchedGlobally }) => searchedGlobally,
);

export const selectSimilarSurveysGlobalSearchUsed = createSelector(
  selectSimilarSurveysState,
  ({ globalSearchUsed }) => globalSearchUsed,
);
//!SECTION

//SECTION EPC
export const selectEpc = createSelector(selectActiveChild, ({ epc }) => epc);

export const selectEpcPlatformByCountry = createSelector(
  selectEpc,
  ({ platformByCountry }) => platformByCountry,
);

export const selectEpcEstimate = createSelector(
  selectEpc,
  ({ estimate }) => estimate,
);

export const selectEpcActual = createSelector(
  selectEpc,
  ({ actual }) => actual,
);

export const selectEpcRequestedQuery = createSelector(
  selectEpc,
  ({ requestedQuery }) => requestedQuery,
);

/** Resolves the platform figure for whichever country the form currently holds. */
export const selectEpcPlatformForSelectedCountry = createSelector(
  selectEpcPlatformByCountry,
  selectMetadataForm,
  (platformByCountry, metadata): Nullable<number> =>
    platformByCountry[epcPlatformCountryCode(metadata, DEFAULT_COUNTRY_CODE)] ??
    null,
);

export const selectEpcValues = createSelector(
  selectEpcPlatformForSelectedCountry,
  selectEpcEstimate,
  selectEpcActual,
  (platform, estimate, actual): SurveyEpcValues => ({
    platform,
    estimate,
    actual,
  }),
);
//!SECTION

// Per child — the same projections over a child by key.
export const selectPricingFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, SurveyCreationPricingState> =>
  pricingOf(selectChildFor(key));

export const selectFeasibilityCpiFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, number> => feasibilityCpiOf(selectChildFor(key));
